import { useEffect, useMemo, useRef, useState } from "react";
import qrcode from "qrcode-generator";
import Shell from "../components/Shell.jsx";
import ContactActions from "../components/SocialIcons.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { api } from "../lib/api.js";
import { startHandoff } from "../lib/handoff.js";
import { useWakeLock } from "../lib/useWakeLock.js";
import { Field, Segmented, SaveButton, inputCls } from "../components/ui.jsx";

export const MODES = [
  { value: "exchange", label: "🤝 Exchange" },
  { value: "card", label: "📇 Card" },
  { value: "site", label: "🌐 Site" },
  { value: "music", label: "🎵 Music" },
];

const MUSIC_URL = "https://aiengines.work/music/";

// vCard text-value escaping (RFC 6350): backslash, comma, semicolon, newline.
const esc = (s = "") => s.replace(/\\/g, "\\\\").replace(/,/g, "\\,").replace(/;/g, "\\;").replace(/\n/g, "\\n");

function nameParts(card) {
  if (card.first_name || card.last_name) return { given: card.first_name || "", family: card.last_name || "" };
  const parts = (card.name || "").trim().split(/\s+/);
  return {
    given: parts.length > 1 ? parts.slice(0, -1).join(" ") : parts[0] || "",
    family: parts.length > 1 ? parts[parts.length - 1] : "",
  };
}

export function buildVcf(card) {
  const { given, family } = nameParts(card);
  return [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${esc(family)};${esc(given)};;;`,
    `FN:${esc(card.name || `${given} ${family}`.trim())}`,
    card.company && `ORG:${esc(card.company)}`,
    card.title && `TITLE:${esc(card.title)}`,
    card.phone && `TEL;TYPE=CELL:${esc(card.phone)}`,
    card.email && `EMAIL:${esc(card.email)}`,
    card.website && `URL:${esc(card.website)}`,
    card.facebook && `X-SOCIALPROFILE;TYPE=facebook:${esc(card.facebook)}`,
    card.linkedin && `X-SOCIALPROFILE;TYPE=linkedin:${esc(card.linkedin)}`,
    card.twitter && `X-SOCIALPROFILE;TYPE=twitter:${esc(card.twitter)}`,
    card.instagram && `X-SOCIALPROFILE;TYPE=instagram:${esc(card.instagram)}`,
    "END:VCARD",
  ]
    .filter(Boolean)
    .join("\r\n");
}

function Qr({ text }) {
  const svg = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(text);
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 3, scalable: true });
  }, [text]);
  // Self-generated SVG from our own data — safe to inject.
  return (
    <div
      className="mx-auto aspect-square w-full max-w-[280px] overflow-hidden rounded-xl bg-white [&>svg]:h-full [&>svg]:w-full"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

// The swipeable QR panel set, shared by the normal page and hand-off mode
// (which shows nothing else). `showLink` hides the tap-out exchange link when
// the phone is in someone else's hands.
export function QrPanels({ mode, onMode, card, exchangeUrl, showLink = true }) {
  // Swipe between QR panels — the tabs work too, but a thumb mid-conversation
  // shouldn't have to aim at one.
  const touchX = useRef(null);
  const site = card?.website || "https://aiengines.work";

  return (
    <>
      <Segmented options={MODES} value={mode} onChange={onMode} />

      <section
        className="rounded-2xl bg-white p-5 shadow-sm"
        onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchX.current === null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          touchX.current = null;
          if (Math.abs(dx) < 48) return;
          const i = MODES.findIndex((m) => m.value === mode);
          const next = dx < 0 ? Math.min(i + 1, MODES.length - 1) : Math.max(i - 1, 0);
          onMode(MODES[next].value);
        }}
      >
        {mode === "exchange" ? (
          exchangeUrl ? (
            <>
              <Qr text={exchangeUrl} />
              <p className="mt-3 text-center text-sm text-slate-500">
                They scan this → they save your card <em>and</em> can leave theirs. Whoever shares back lands in your
                contacts with a follow-up seven days out.
              </p>
              {showLink && (
                <a
                  href={exchangeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 block truncate text-center text-xs text-navy-700 underline"
                >
                  {exchangeUrl.replace(/^https?:\/\//, "")}
                </a>
              )}
            </>
          ) : (
            <p className="py-10 text-center text-sm text-slate-400">Preparing your exchange link…</p>
          )
        ) : mode === "card" ? (
          card ? (
            <>
              <Qr text={buildVcf(card)} />
              <p className="mt-3 text-center text-sm text-slate-500">
                They scan this with their camera → your contact card pops up to save. Nothing comes back to you — use
                Exchange for that.
              </p>
            </>
          ) : (
            <p className="py-10 text-center text-sm text-slate-400">Card not loaded.</p>
          )
        ) : mode === "music" ? (
          <>
            <Qr text={MUSIC_URL} />
            <p className="mt-3 text-center text-sm text-slate-500">
              Scanning opens <span className="font-medium text-slate-700">aiengines.work/music</span> — the music
              you've made with AI.
            </p>
          </>
        ) : (
          <>
            <Qr text={site} />
            <p className="mt-3 text-center text-sm text-slate-500">
              Scanning opens <span className="font-medium text-slate-700">{site.replace(/^https?:\/\//, "")}</span>
            </p>
          </>
        )}
      </section>
    </>
  );
}

function CardForm({ initial, onSaved }) {
  // Server stores empty fields as null — controlled inputs need "".
  const [f, setF] = useState(() => Object.fromEntries(Object.entries(initial).map(([k, v]) => [k, v ?? ""])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api("/api/me/card", { method: "PUT", body: f });
      onSaved(res.card);
    } catch (err) {
      setError(err.message || "Save failed");
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl bg-white p-5 shadow-sm">
      <p className="text-sm font-semibold text-slate-700">Your card details</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name">
          <input value={f.first_name} onChange={set("first_name")} className={inputCls} />
        </Field>
        <Field label="Last name">
          <input value={f.last_name} onChange={set("last_name")} className={inputCls} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Title">
          <input value={f.title} onChange={set("title")} className={inputCls} placeholder="Founder" />
        </Field>
        <Field label="Company">
          <input value={f.company} onChange={set("company")} className={inputCls} />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Phone">
          <input value={f.phone} onChange={set("phone")} className={inputCls} inputMode="tel" />
        </Field>
        <Field label="Email">
          <input value={f.email} onChange={set("email")} className={inputCls} type="email" />
        </Field>
      </div>
      <Field label="Website">
        <input value={f.website} onChange={set("website")} className={inputCls} inputMode="url" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Facebook">
          <input value={f.facebook} onChange={set("facebook")} className={inputCls} placeholder="URL or handle" inputMode="url" />
        </Field>
        <Field label="LinkedIn">
          <input value={f.linkedin} onChange={set("linkedin")} className={inputCls} placeholder="URL or handle" inputMode="url" />
        </Field>
        <Field label="X / Twitter">
          <input value={f.twitter} onChange={set("twitter")} className={inputCls} placeholder="URL or @handle" inputMode="url" />
        </Field>
        <Field label="Instagram">
          <input value={f.instagram} onChange={set("instagram")} className={inputCls} placeholder="URL or @handle" inputMode="url" />
        </Field>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <SaveButton busy={busy}>Save card</SaveButton>
    </form>
  );
}

export default function MyCard() {
  const { user } = useAuth();
  const [card, setCard] = useState(undefined); // undefined = loading, null = not set up
  const [mode, setMode] = useState("exchange");
  const [editing, setEditing] = useState(false);
  const [shareNote, setShareNote] = useState("");
  const [exchangeUrl, setExchangeUrl] = useState("");

  // A phone that sleeps mid-scan ends the conversation.
  useWakeLock(!!card && !editing);

  useEffect(() => {
    api("/api/me/card")
      .then((d) => {
        setCard(d.card);
        setExchangeUrl(d.exchange_url || "");
      })
      .catch(() => setCard(null));
  }, []);

  const dn = (user?.display_name || "").trim();
  const dnCut = dn.lastIndexOf(" ");
  const defaults = {
    first_name: dnCut > 0 ? dn.slice(0, dnCut) : dn,
    last_name: dnCut > 0 ? dn.slice(dnCut + 1) : "",
    title: "Founder",
    company: "AI Engines",
    phone: "",
    email: user?.email || "",
    website: "https://aiengines.work",
    facebook: "",
    linkedin: "",
    twitter: "",
    instagram: "",
  };

  async function share() {
    setShareNote("");
    const vcf = buildVcf(card);
    const file = new File([vcf], `${(card.name || "contact").replace(/\s+/g, "-")}.vcf`, { type: "text/vcard" });
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: card.name });
        return;
      }
    } catch (e) {
      if (e.name === "AbortError") return; // user closed the share sheet
    }
    // Fallback: download the .vcf
    const url = URL.createObjectURL(new Blob([vcf], { type: "text/vcard" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    setShareNote("Card file downloaded — open it to add/share.");
  }

  if (card === undefined)
    return (
      <Shell title="My card" back>
        <p className="text-center text-sm text-slate-400">Loading…</p>
      </Shell>
    );

  if (!card || editing) {
    // Older saved cards predate first/last + socials — fill the gaps.
    const initial = { ...defaults, ...(card || {}) };
    if (card && !card.first_name && card.name) {
      const cut = card.name.lastIndexOf(" ");
      initial.first_name = cut > 0 ? card.name.slice(0, cut) : card.name;
      initial.last_name = cut > 0 ? card.name.slice(cut + 1) : "";
    }
    return (
      <Shell title="My card" back>
        <CardForm
          initial={initial}
          onSaved={(c) => {
            setCard(c);
            setEditing(false);
          }}
        />
      </Shell>
    );
  }

  return (
    <Shell title="My card" back>
      <QrPanels mode={mode} onMode={setMode} card={card} exchangeUrl={exchangeUrl} />

      {!user?.demo && (
        <>
          <button
            onClick={() => startHandoff(user?.id)}
            className="w-full rounded-xl border-2 border-navy-600/30 bg-navy-700/5 py-3.5 text-sm font-semibold text-navy-700"
          >
            🔒 Lock here & pass the phone
          </button>
          <p className="-mt-2 text-center text-xs text-slate-400">
            Only the QR screens stay usable — getting back in takes your Face ID or PIN.
          </p>
        </>
      )}

      {mode === "card" && (
        <button onClick={share} className="w-full rounded-xl bg-navy-700 py-3.5 text-sm font-semibold text-white">
          📤 AirDrop / share my card
        </button>
      )}
      {shareNote && <p className="text-center text-xs text-slate-500">{shareNote}</p>}

      <div className="rounded-2xl bg-white p-4 text-sm shadow-sm">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="font-semibold text-slate-800">{card.name}</p>
            <p className="truncate text-xs text-slate-400">
              {[card.title, card.company].filter(Boolean).join(" · ")}
              {card.phone ? ` · ${card.phone}` : ""}
            </p>
          </div>
          <button onClick={() => setEditing(true)} className="shrink-0 text-sm font-semibold text-navy-700">
            ✏️ Edit
          </button>
        </div>
        <div className="mt-3">
          <ContactActions person={card} />
        </div>
      </div>
    </Shell>
  );
}
